import { Heart } from "lucide-react";

import { cn } from "@/lib/utils/cn";

/**
 * "Favourites only" filter — a pressed-state toggle carrying its own count.
 *
 * Shared by the three resource directories so the affordance is identical in
 * each. Renders nothing at a count of zero — there is nothing to narrow to yet —
 * unless it is currently on, so a filter that excludes every favourite cannot
 * yank the control the user is standing on out from under them.
 *
 * Styled with `primary` (the brand's lead-affordance colour), not `destructive`
 * — a favourite is not an error state.
 */
export function FavoritesToggle({
  count,
  on,
  onChange,
  label,
  className,
}: {
  count: number;
  on: boolean;
  onChange: (value: boolean) => void;
  label: string;
  className?: string;
}) {
  if (count === 0 && !on) return null;

  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={() => onChange(!on)}
      title={label}
      className={cn(
        "inline-flex min-h-touch shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-sm font-semibold",
        on ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent",
        className,
      )}
    >
      <Heart className={cn("size-4", on && "fill-primary")} aria-hidden />
      <span className="tabular-nums">{count}</span>
      <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">{label}</span>
    </button>
  );
}
